import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button } from '../design-system';

interface City {
  id: string;
  name: string;
}

/**
 * Admin: the cities a host covers. A host normally works in one city and can't
 * add a second themselves (while trips with several cities are on); this is how
 * an admin allows it. The first city ticked is the host's main city, shown as
 * their label.
 */
export function HostCitiesEditor({ hostId }: { hostId: string }) {
  const [cities, setCities] = useState<City[]>([]);
  // Ticked cities in the order they were ticked (the first is the label city).
  const [picked, setPicked] = useState<string[]>([]);
  const [saved, setSaved] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [{ data: dests }, { data: links }] = await Promise.all([
        supabase.from('destinations').select('id, name').eq('active', true).order('sort_order'),
        supabase.from('host_destinations').select('destination_id').eq('host_id', hostId).order('created_at'),
      ]);
      if (cancelled) return;
      const ids = ((links as { destination_id: string }[] | null) ?? []).map((l) => l.destination_id);
      setCities((dests as City[] | null) ?? []);
      setPicked(ids);
      setSaved(ids);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [hostId]);

  function toggle(id: string) {
    setState('idle');
    setPicked((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }

  async function save() {
    setState('saving');
    const { error } = await supabase.rpc('admin_set_host_cities', { p_host_id: hostId, p_destination_ids: picked });
    if (error) {
      console.error('admin_set_host_cities failed:', error);
      setState('error');
      return;
    }
    setSaved(picked);
    setState('done');
  }

  if (loading) return null;
  const changed = picked.join() !== saved.join();

  return (
    <div className="col-span-2 sm:col-span-3 flex flex-col gap-2">
      <span className="text-[10px] text-app-muted uppercase tracking-wide">Cities this host covers</span>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {cities.map((c) => (
          <label key={c.id} className="inline-flex items-center gap-1.5 text-sm text-app-fg">
            <input type="checkbox" checked={picked.includes(c.id)} onChange={() => toggle(c.id)} />
            {c.name}
          </label>
        ))}
      </div>
      <p className="text-xs text-app-muted">
        A host normally works in one city. Tick more than one only on purpose. The first one ticked is their main city.
      </p>
      <div className="flex items-center gap-3">
        <Button size="sm" variant="secondary" onClick={() => void save()} disabled={!changed || state === 'saving'}>
          {state === 'saving' ? 'Saving…' : 'Save cities'}
        </Button>
        {state === 'done' && <span className="text-xs text-emerald-500">Saved</span>}
        {state === 'error' && <span role="alert" className="text-xs text-crimson">Couldn't save the cities. Try again.</span>}
      </div>
    </div>
  );
}
